import { useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Scenario } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand } from "@kundeninfo/ui";
import { Eingabe, Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { errorProp, fieldErrorInsteadOf409 } from "../../components/fieldErrors.js";
import { AMOUNT_FIELD_MAX, amountField, amountForApi } from "../suppliers/shared.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

const S = T.scenario;
/** Die Grenzen des Schemas (`ScenarioCreate`); web importiert die Domäne nicht. */
const NAME_MAX = 100, DESCRIPTION_MAX = 1000;
/** Keine fremden Stände: Der Name steht am Feld, ein Wiederholungsfall als Hinweis (`useFormularZustand` liest sonst jede 409 als Versionskonflikt). */
const NOT_A_CONFLICT: readonly string[] = ["SCENARIO_NAME_TAKEN", "TICKET_LOCK_RETRY"];

export type ScenarioPanelMode = { kind: "create" } | { kind: "duplicate"; from: Scenario } | { kind: "edit"; scenario: Scenario }
  | { kind: "fromDraft"; from: Scenario | null; changes: readonly { ref: string; enabled?: boolean; freeze?: boolean }[] };

type Head = { name: string; description: string; minimum: string; copyContent: boolean };

const headOf = (s: Scenario | null, name: string): Head =>
  ({ name, description: s?.description ?? "", minimum: s === null || s.minimumBalance === null ? "" : amountField(s.minimumBalance), copyContent: true });

/** Der Kopf einer Kopie kommt nur aus dem Rumpf (Bericht Aufgabe 9): Das Panel füllt ihn vor; der Zusatz „(Kopie)“ bleibt auch an einem langen Namen stehen. */
function startOf(mode: ScenarioPanelMode): Head {
  switch (mode.kind) {
    case "create": return headOf(null, "");
    case "duplicate": return headOf(mode.from, S.copyName(mode.from.name.slice(0, NAME_MAX - S.copyName("").length)));
    case "edit": return headOf(mode.scenario, mode.scenario.name);
    case "fromDraft": return headOf(mode.from, "");
  }
}

/**
 * EIN SZENARIO ANLEGEN ODER ÄNDERN (Spec „Dialoge › Szenario“, Plan Aufgabe 11): Modalpanel rechts, „Speichern“ ist die Bestätigung. Name, Beschreibung und Mindestsaldo, leer
 * entfernt ihn, negativ ist erlaubt (Kontokorrent); beim Duplizieren dazu „Abweichungen und eigene Planpositionen übernehmen“. Der Entwurf kommt beim Weg „Als neues Szenario …“
 * als `changes` mit. Ein vergebener Name steht am Feld; ein fremder Stand zeigt „Neu laden“ und übernimmt den Satz aus der 409, ohne Leseweg.
 */
export function ScenarioPanel(p: {
  mode: ScenarioPanelMode; companyId: string; client: Pick<ApiClient, "createScenario" | "updateScenario">;
  onSaved: (s: Scenario) => void; onCancel: () => void;
}): ReactElement {
  const { mode } = p;
  const [version, setVersion] = useState(mode.kind === "edit" ? mode.scenario.version : 0);
  const [start, setStart] = useState(() => startOf(mode));
  const [head, setHead] = useState(start);
  const [tried, setTried] = useState(false);
  const latest = useRef<Scenario | null>(null);
  const name = head.name.trim(), description = head.description.trim(), minimumEmpty = head.minimum.trim() === "";
  const minimumBalance = amountForApi(head.minimum, { signed: true });
  const fields = { name, description: description === "" ? null : description, minimumBalance };

  function write(): Promise<Scenario> {
    switch (mode.kind) {
      case "edit": return p.client.updateScenario(mode.scenario.id, { version, ...fields });
      case "create": return p.client.createScenario({ companyId: p.companyId, ...fields });
      case "duplicate": return p.client.createScenario({ companyId: p.companyId, ...fields, copyFromId: mode.from.id, copyContent: head.copyContent });
      case "fromDraft": return p.client.createScenario({ companyId: p.companyId, ...fields, ...(mode.from === null ? {} : { copyFromId: mode.from.id }), changes: [...mode.changes] });
    }
  }
  const form = useFormularZustand<Scenario>({
    speichern: () => fieldErrorInsteadOf409(async () => {
      try {
        return await write();
      } catch (e) {
        if (e instanceof ApiError && e.conflict !== undefined) latest.current = e.conflict.current as Scenario;
        throw e;
      }
    }, NOT_A_CONFLICT),
    neuLaden: async () => {
      const current = latest.current;
      if (current === null) return;
      const next = headOf(current, current.name);
      setVersion(current.version);
      setStart(next);
      setHead(next);
    },
    bekannteFelder: ["name", "description", "minimumBalance"],
  });
  const dirty = JSON.stringify(head) !== JSON.stringify(start);
  useBeforeUnload(dirty);
  const set = (change: Partial<Head>): void => setHead((h) => ({ ...h, ...change }));
  const minimumInvalid = !minimumEmpty && minimumBalance === null;

  async function save(): Promise<void> {
    setTried(true);
    if (name === "" || minimumInvalid) return;
    const saved = await form.aktion();
    if (saved !== undefined) p.onSaved(saved);
  }
  async function leave(): Promise<void> {
    if (!form.laeuft && await darfAbbrechen(dirty)) p.onCancel();
  }

  return (
    <Modal titel={S.title[mode.kind]} onAbbrechen={() => { void leave(); }} abbrechenGesperrt={form.laeuft}>
      {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
      <Formular beschriftung="oben" {...form.formularProps}
        hinweis={form.zustand === "fremder-stand" ? <span>{T.dialogs.changedElsewhere}</span> : undefined}>
        <div className="liq__mask">
          {mode.kind === "fromDraft" && <p className="liq__muted">{S.fromDraftHint(mode.changes.length)}</p>}
          <Eingabe beschriftung={S.name} wert={head.name} pflicht maxLength={NAME_MAX}
            {...errorProp(form.feldFehler.name ?? (tried && name === "" ? T.dialogs.nameRequired : undefined))} onGeaendert={(e) => set({ name: e.wert })} />
          <Eingabe beschriftung={S.description} mehrzeilig wert={head.description} maxLength={DESCRIPTION_MAX} {...errorProp(form.feldFehler.description)}
            onGeaendert={(e) => set({ description: e.wert })} />
          <Eingabe art="dezimal" beschriftung={S.minimumBalance} einheit={T.dialogs.euroUnit} wert={head.minimum} maxLength={AMOUNT_FIELD_MAX} hilfe={S.minimumBalanceHint}
            {...errorProp(form.feldFehler.minimumBalance ?? (tried && minimumInvalid ? T.dialogs.amountInvalid : undefined))} onGeaendert={(e) => set({ minimum: e.wert })} />
          {mode.kind === "duplicate" && <Schalter beschriftung={S.copyContent} an={head.copyContent} onGeaendert={(e) => set({ copyContent: e.an })} />}
        </div>
      </Formular>
      <ModalFuss speichernText={T.dialogs.save} speichernGesperrt={form.zustand !== "bereit"} abbrechenGesperrt={form.laeuft}
        onSpeichern={() => { void save(); }} onAbbrechen={() => { void leave(); }} />
    </Modal>
  );
}
